import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { Plus, Pencil, Trash2, X, Save, Loader2 } from "lucide-react";
import { StatusPill } from "./GestionaleShell";

/**
 * Generic CRUD table — driven by a `fields` config + endpoint base path.
 * fields: [{ key, label, type: 'text'|'textarea'|'number'|'select'|'date'|'bool', options?, span?, hideInTable?, statusTone?, format? }]
 */
export default function GestCrudTable({
  endpoint,
  fields,
  itemLabel,
  emptyHint,
  onItemsLoaded,
  rowSummary,
  testIdPrefix,
}) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | obj (edit)
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const reload = async () => {
    setLoading(true);
    try {
      const r = await api.get(endpoint);
      const list = r.data.items || [];
      setItems(list);
      if (onItemsLoaded) onItemsLoaded(r.data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore caricamento");
    } finally { setLoading(false); }
  };

  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [endpoint]);

  const blankRow = () => Object.fromEntries(fields.map(f => [f.key, f.defaultValue !== undefined ? f.defaultValue : (f.type === "number" ? 0 : f.type === "bool" ? false : "")]));

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true); setError("");
    try {
      const payload = {};
      for (const f of fields) {
        let v = editing[f.key];
        if (f.type === "number") v = parseFloat(v) || 0;
        if (f.type === "bool") v = !!v;
        payload[f.key] = v ?? (f.type === "number" ? 0 : f.type === "bool" ? false : "");
      }
      if (editing.id) {
        await api.put(`${endpoint}/${editing.id}`, payload);
      } else {
        await api.post(endpoint, payload);
      }
      setEditing(null);
      await reload();
    } catch (e) {
      setError(e?.response?.data?.detail || "Errore salvataggio");
    } finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!window.confirm(`Eliminare ${itemLabel}?`)) return;
    try { await api.delete(`${endpoint}/${id}`); await reload(); }
    catch { setError("Errore eliminazione"); }
  };

  const tableFields = fields.filter(f => !f.hideInTable);

  return (
    <div data-testid={`${testIdPrefix}-crud`} className="bg-white border border-stone-200 rounded-sm shadow-sm">
      <div className="flex items-center justify-between gap-3 p-4 border-b border-stone-200">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A88A4A] font-bold">Elenco {itemLabel}</div>
          <div className="text-xs text-stone-500 mt-1">
            {loading ? "Caricamento…" : `${items.length} record${items.length === 1 ? "" : ""} totali`}
          </div>
        </div>
        <button
          data-testid={`${testIdPrefix}-new`}
          onClick={() => setEditing(blankRow())}
          className="inline-flex items-center gap-2 px-4 py-2 bg-[#C9A961] hover:bg-[#A88A4A] text-white text-[10px] uppercase tracking-[0.2em] font-bold"
        >
          <Plus className="h-3.5 w-3.5" /> Nuovo {itemLabel}
        </button>
      </div>

      {error && <div className="m-4 p-3 text-xs text-rose-700 bg-rose-50 border border-rose-200">{error}</div>}

      {loading ? (
        <div className="p-10 text-center text-stone-500"><Loader2 className="h-5 w-5 animate-spin mx-auto" /></div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center">
          <div className="text-stone-500 text-sm mb-3">Nessun record presente.</div>
          {emptyHint && <div className="text-xs text-stone-400">{emptyHint}</div>}
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-stone-50 border-b border-stone-200">
              <tr>
                {tableFields.map(f => (
                  <th key={f.key} className="text-left px-4 py-3 text-[9px] uppercase tracking-[0.2em] text-stone-500 font-bold">{f.label}</th>
                ))}
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.id} data-testid={`${testIdPrefix}-row-${it.id}`} className="border-b border-stone-100 hover:bg-stone-50/50">
                  {tableFields.map(f => {
                    const raw = it[f.key];
                    let display = raw;
                    if (f.format) display = f.format(raw, it);
                    else if (f.type === "bool") display = raw ? "Sì" : "No";
                    else if (f.type === "select" && f.options) {
                      const opt = f.options.find(o => o.value === raw);
                      if (opt && f.statusTone) {
                        return <td key={f.key} className="px-4 py-3"><StatusPill tone={opt.tone || "stone"} label={opt.label} /></td>;
                      }
                      display = opt?.label || raw;
                    }
                    return <td key={f.key} className="px-4 py-3 text-stone-800">{display ?? "—"}</td>;
                  })}
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    <button data-testid={`${testIdPrefix}-edit-${it.id}`} onClick={() => setEditing(it)} className="text-stone-500 hover:text-[#C9A961] p-1.5 mr-1"><Pencil className="h-3.5 w-3.5" /></button>
                    <button data-testid={`${testIdPrefix}-delete-${it.id}`} onClick={() => remove(it.id)} className="text-stone-400 hover:text-rose-600 p-1.5"><Trash2 className="h-3.5 w-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {rowSummary && items.length > 0 && (
        <div className="p-4 border-t border-stone-200 bg-stone-50/50 text-xs text-stone-700">
          {rowSummary(items)}
        </div>
      )}

      {/* Slide-over editor */}
      {editing && (
        <div className="fixed inset-0 z-50 flex" data-testid={`${testIdPrefix}-editor`}>
          <div className="flex-1 bg-black/40" onClick={() => !saving && setEditing(null)} />
          <form onSubmit={submit} className="w-full max-w-xl bg-[#faf7f0] shadow-2xl flex flex-col">
            <div className="flex items-center justify-between p-5 border-b border-stone-300">
              <div>
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#A88A4A] font-bold">{editing.id ? "Modifica" : "Nuovo"}</div>
                <h3 className="editorial-heading text-2xl text-stone-900 mt-1">{itemLabel}</h3>
              </div>
              <button type="button" onClick={() => setEditing(null)} disabled={saving} className="p-2 text-stone-500 hover:text-stone-900"><X className="h-5 w-5" /></button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 grid grid-cols-2 gap-4">
              {fields.map(f => (
                <div key={f.key} className={f.span === 2 || f.type === "textarea" ? "col-span-2" : ""}>
                  <label className="block text-[9px] uppercase tracking-[0.25em] text-stone-500 font-bold mb-1.5">{f.label}{f.required && " *"}</label>
                  {f.type === "textarea" ? (
                    <textarea
                      value={editing[f.key] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 text-sm bg-white border border-stone-300 focus:border-[#C9A961] focus:ring-1 focus:ring-[#C9A961] outline-none"
                    />
                  ) : f.type === "select" ? (
                    <select
                      value={editing[f.key] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                      className="w-full px-3 py-2 text-sm bg-white border border-stone-300 focus:border-[#C9A961] outline-none"
                    >
                      {f.options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : f.type === "bool" ? (
                    <label className="flex items-center gap-2 mt-2">
                      <input type="checkbox" checked={!!editing[f.key]} onChange={(e) => setEditing({ ...editing, [f.key]: e.target.checked })} />
                      <span className="text-sm text-stone-700">{f.checkboxLabel || "Sì"}</span>
                    </label>
                  ) : (
                    <input
                      type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                      step={f.type === "number" ? (f.step || "0.01") : undefined}
                      value={editing[f.key] ?? ""}
                      onChange={(e) => setEditing({ ...editing, [f.key]: e.target.value })}
                      required={f.required}
                      placeholder={f.placeholder || ""}
                      className="w-full px-3 py-2 text-sm bg-white border border-stone-300 focus:border-[#C9A961] focus:ring-1 focus:ring-[#C9A961] outline-none"
                    />
                  )}
                  {f.help && <p className="text-[10px] text-stone-400 mt-1">{f.help}</p>}
                </div>
              ))}
            </div>
            <div className="p-5 border-t border-stone-300 flex items-center justify-between bg-white">
              <button type="button" onClick={() => setEditing(null)} disabled={saving} className="text-xs uppercase tracking-[0.2em] text-stone-500 hover:text-stone-900">Annulla</button>
              <button
                type="submit"
                disabled={saving}
                data-testid={`${testIdPrefix}-save`}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#C9A961] hover:bg-[#A88A4A] disabled:opacity-50 text-white text-[10px] uppercase tracking-[0.25em] font-bold"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />} Salva
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
